import { useEffect, useMemo, useRef } from 'react';
import type { UserAppDraft } from '@api/appBuilder';
import type { AppRunDetail } from '@api/apps';
import { useAppRunController, type AppRunController } from '../../appFrame/useAppRunController';
import { useBuilderRunHost } from './useBuilderRunHost';

const isOwnTestRun = (run: AppRunDetail, appKey: string): boolean =>
  run.appKey === appKey && run.isTest === true && !run.readOnly;

const isBusy = (controller: AppRunController, startPending: boolean): boolean =>
  controller.isBusy ||
  controller.submitting ||
  controller.isLaunching ||
  startPending ||
  controller.run?.status === 'WAITING_ON_USER' ||
  controller.live.pendingApprovals.length > 0;

// A run is never cancelled for a page change. Once idle it is dropped only if this screen launched it or showed it
// on the page it ran; one opened from an old link stays, flagged.
export const useBuilderRun = (draft: UserAppDraft, onProblems: (problems: readonly string[]) => void) => {
  const host = useBuilderRunHost(draft, onProblems);
  const page = draft.preview.presentation?.page ?? null;
  const runnable = draft.status !== 'DISABLED' && page !== null;
  const runHost = useMemo(
    () => (runnable ? host : { ...host, runId: undefined, composeManifest: null }),
    [host, runnable],
  );
  const controller = useAppRunController(runHost);

  const { run } = controller;
  const startPending = run !== null && run.status === 'DRAFT' && host.autostartFor(run.runId) !== null;
  const busy = isBusy(controller, startPending);
  const stale = run !== null && run.manifest.presentation?.page !== page;
  const unusable = controller.notFound || (run !== null && !isOwnTestRun(run, draft.appKey));
  const dropIfWatched = runHost.runId !== undefined && (unusable || (stale && !busy));

  const watchedRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (run && (!stale || startPending)) watchedRef.current.add(run.runId);
  }, [run, stale, startPending]);

  const droppedRef = useRef<string | null>(null);
  const { startOver } = host;
  useEffect(() => {
    const runId = runHost.runId;
    if (!dropIfWatched || !runId || droppedRef.current === runId) return;
    if (!unusable && !watchedRef.current.has(runId)) return;
    droppedRef.current = runId;
    startOver();
  }, [dropIfWatched, runHost.runId, startOver, unusable]);

  return { controller: runnable ? controller : null, stale };
};
